"use client";

import { useEffect, useState, useRef } from "react";
import Link from "next/link";
import { usePathname } from "next/navigation";

const NAV_LINKS = [
  { href: "/templates", label: "Templates" },
  { href: "/free-esignature", label: "Features" },
  { href: "/pricing", label: "Pricing" },
  { href: "/verify", label: "Verify" },
];

const DEVELOPER_LINKS = [
  {
    href: "/developers",
    label: "API Reference",
    desc: "REST endpoints, authentication & webhooks",
    icon: "M17.25 6.75L22.5 12l-5.25 5.25m-10.5 0L1.5 12l5.25-5.25m7.5-3l-4.5 16.5",
  },
  {
    href: "/developers/verify",
    label: "Verification API",
    desc: "Public endpoints to verify signed documents",
    icon: "M9 12.75L11.25 15 15 9.75m-3-7.036A11.959 11.959 0 013.598 6 11.99 11.99 0 003 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285z",
  },
  {
    href: "/developers/mcp",
    label: "MCP Server",
    desc: "18 tools for Claude, Cursor & AI agents",
    icon: "M8.25 3v1.5M4.5 8.25H3m18 0h-1.5M4.5 12H3m18 0h-1.5m-15 3.75H3m18 0h-1.5M8.25 19.5V21M12 3v1.5m0 15V21m3.75-18v1.5m0 15V21m-9-1.5h9a2.25 2.25 0 002.25-2.25V6.75A2.25 2.25 0 0015.75 4.5h-9A2.25 2.25 0 004.5 6.75v10.5A2.25 2.25 0 006.75 19.5z",
  },
  {
    href: "/developers/embed",
    label: "Embed Widget",
    desc: "Drop signing into your app with one script tag",
    icon: "M13.5 16.875h3.375m0 0h3.375m-3.375 0V13.5m0 3.375v3.375M6 10.5h2.25a2.25 2.25 0 002.25-2.25V6a2.25 2.25 0 00-2.25-2.25H6A2.25 2.25 0 003.75 6v2.25A2.25 2.25 0 006 10.5zm0 9.75h2.25A2.25 2.25 0 0010.5 18v-2.25a2.25 2.25 0 00-2.25-2.25H6a2.25 2.25 0 00-2.25 2.25V18A2.25 2.25 0 006 20.25zm9.75-9.75H18a2.25 2.25 0 002.25-2.25V6A2.25 2.25 0 0018 3.75h-2.25A2.25 2.25 0 0013.5 6v2.25a2.25 2.25 0 002.25 2.25z",
  },
  {
    href: "/developers/examples",
    label: "Code Examples",
    desc: "Python, Node.js, cURL & n8n recipes",
    icon: "M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m5.231 13.481L15 17.25m-4.5-15H5.625c-.621 0-1.125.504-1.125 1.125v16.5c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9zm3.75 11.625a2.625 2.625 0 11-5.25 0 2.625 2.625 0 015.25 0z",
  },
];

const TOOL_CATEGORIES = [
  {
    label: "Sign & Fill",
    tools: [
      { href: "/sign-pdf-online-free", label: "Sign PDF Online" },
      { href: "/tools/signature-generator", label: "Signature Generator" },
      { href: "/tools/add-signature-to-pdf", label: "Add Signature to PDF" },
      { href: "/tools/fill-and-sign-pdf", label: "Fill & Sign PDF" },
    ],
  },
  {
    label: "Organize",
    tools: [
      { href: "/tools/merge-pdf", label: "Merge PDF" },
      { href: "/tools/split-pdf", label: "Split PDF" },
      { href: "/tools/rotate-pdf", label: "Rotate PDF" },
    ],
  },
  {
    label: "Optimize",
    tools: [
      { href: "/tools/compress-pdf", label: "Compress PDF" },
      { href: "/tools/pdf-watermark", label: "PDF Watermark" },
      { href: "/tools/unlock-pdf", label: "Unlock PDF" },
      { href: "/tools/add-page-numbers", label: "Add Page Numbers" },
    ],
  },
  {
    label: "Convert",
    tools: [
      { href: "/tools/pdf-to-image", label: "PDF to Image" },
      { href: "/tools/image-to-pdf", label: "Image to PDF" },
    ],
  },
];

const COMPARE_LINKS = [
  { href: "/docusign-alternative", label: "vs DocuSign", desc: "Full comparison with the market leader" },
  { href: "/pandadoc-alternative", label: "vs PandaDoc", desc: "Proposals, quotes & e-signatures" },
  { href: "/hellosign-alternative", label: "vs Dropbox Sign", desc: "Formerly HelloSign — free tier gone" },
  { href: "/adobe-sign-alternative", label: "vs Adobe Sign", desc: "No Acrobat subscription needed" },
];

const MORE_LINKS = [
  {
    href: "/about",
    label: "About Us",
    desc: "SignForge is a CTRL 18 product — meet the team",
    icon: "M18 18.72a9.094 9.094 0 003.741-.479 3 3 0 00-4.682-2.72m.94 3.198l.001.031c0 .225-.012.447-.037.666A11.944 11.944 0 0112 21c-2.17 0-4.207-.576-5.963-1.584A6.062 6.062 0 016 18.719m12 0a5.971 5.971 0 00-.941-3.197m0 0A5.995 5.995 0 0012 12.75a5.995 5.995 0 00-5.058 2.772m0 0a3 3 0 00-4.681 2.72 8.986 8.986 0 003.74.477m.94-3.197a5.971 5.971 0 00-.94 3.197M15 6.75a3 3 0 11-6 0 3 3 0 016 0zm6 3a2.25 2.25 0 11-4.5 0 2.25 2.25 0 014.5 0zm-13.5 0a2.25 2.25 0 11-4.5 0 2.25 2.25 0 014.5 0z",
  },
  {
    href: "/security-compliance",
    label: "Security & Compliance",
    desc: "Encryption, audit trails & legal compliance",
    icon: "M16.5 10.5V6.75a4.5 4.5 0 10-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 002.25-2.25v-6.75a2.25 2.25 0 00-2.25-2.25H6.75a2.25 2.25 0 00-2.25 2.25v6.75a2.25 2.25 0 002.25 2.25z",
  },
  {
    href: "/contact",
    label: "Contact Us",
    desc: "Get in touch — support, billing & partnerships",
    icon: "M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0L3.32 8.91a2.25 2.25 0 01-1.07-1.916V6.75",
  },
  {
    href: "/terms",
    label: "Terms of Service",
    desc: "Your rights and obligations",
    icon: "M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z",
  },
  {
    href: "/privacy",
    label: "Privacy Policy",
    desc: "How we protect your data",
    icon: "M9 12.75L11.25 15 15 9.75m-3-7.036A11.959 11.959 0 013.598 6 11.99 11.99 0 003 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285z",
  },
  {
    href: "/cancellation-refunds",
    label: "Refunds",
    desc: "Cancellation & refund policy",
    icon: "M2.25 18.75a60.07 60.07 0 0115.797 2.101c.727.198 1.453-.342 1.453-1.096V18.75M3.75 4.5v.75A.75.75 0 013 6h-.75m0 0v-.375c0-.621.504-1.125 1.125-1.125H20.25M2.25 6v9m18-10.5v.75c0 .414.336.75.75.75h.75m-1.5-1.5h.375c.621 0 1.125.504 1.125 1.125v9.75c0 .621-.504 1.125-1.125 1.125h-.375m1.5-1.5H21a.75.75 0 00-.75.75v.75m0 0H3.75m0 0h-.375a1.125 1.125 0 01-1.125-1.125V15m1.5 1.5v-.75A.75.75 0 003 15h-.75M15 10.5a3 3 0 11-6 0 3 3 0 016 0zm3 0h.008v.008H18V10.5zm-12 0h.008v.008H6V10.5z",
  },
  {
    href: "/shipping",
    label: "Shipping & Delivery",
    desc: "Digital delivery — instant access",
    icon: "M3.75 13.5l10.5-11.25L12 10.5h8.25L9.75 21.75 12 13.5H3.75z",
  },
];

export default function PublicNav({ forceSolid = false }: { forceSolid?: boolean }) {
  const pathname = usePathname();
  const [scrolled, setScrolled] = useState(forceSolid);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [toolsOpen, setToolsOpen] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);
  const [devsOpen, setDevsOpen] = useState(false);
  const toolsRef = useRef<HTMLDivElement>(null);
  const moreRef = useRef<HTMLDivElement>(null);
  const devsRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onScroll = () => setScrolled(forceSolid || window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Close dropdowns on outside click
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (toolsRef.current && !toolsRef.current.contains(e.target as Node)) {
        setToolsOpen(false);
      }
      if (moreRef.current && !moreRef.current.contains(e.target as Node)) {
        setMoreOpen(false);
      }
      if (devsRef.current && !devsRef.current.contains(e.target as Node)) {
        setDevsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  const isToolsActive = pathname.startsWith("/tools") || pathname === "/sign-pdf-online-free";
  const isDevsActive = pathname.startsWith("/developers");
  const isCompareActive = COMPARE_LINKS.some((l) => pathname === l.href);
  const isMoreActive = MORE_LINKS.some((l) => pathname === l.href) || isCompareActive;

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-white/80 backdrop-blur-xl border-b border-slate-200/60 shadow-sm"
          : "bg-transparent"
      }`}
    >
      <div className="mx-auto max-w-6xl flex items-center justify-between px-6 py-3.5 sm:px-10">
        {/* Logo */}
        <div className="flex items-center gap-8">
          <Link href="/" className="flex items-center gap-0.5">
            <span
              className={`font-display text-xl font-bold tracking-tight transition-colors duration-300 ${
                scrolled ? "text-ink" : "text-white"
              }`}
            >
              Sign
            </span>
            <span className="font-display text-xl font-bold tracking-tight text-[var(--color-gold)]">
              Forge
            </span>
          </Link>

          {/* Desktop links */}
          <div className="hidden lg:flex items-center gap-1">
            {NAV_LINKS.map((link) => {
              const isActive = pathname === link.href;
              return (
                <Link
                  key={link.href}
                  href={link.href}
                  className={`relative px-3 py-1.5 text-sm rounded-md transition-all duration-200 whitespace-nowrap ${
                    isActive
                      ? scrolled
                        ? "text-ink font-medium bg-slate-100/80"
                        : "text-white font-medium bg-white/10"
                      : scrolled
                      ? "text-slate-500 hover:text-ink hover:bg-slate-50"
                      : "text-white/60 hover:text-white hover:bg-white/5"
                  }`}
                >
                  {link.label}
                  {isActive && (
                    <span className="absolute -bottom-0.5 left-1/2 -translate-x-1/2 w-5 h-[2px] rounded-full bg-[var(--color-gold)]" />
                  )}
                </Link>
              );
            })}

            {/* Developers dropdown */}
            <div className="relative" ref={devsRef}>
              <button
                onClick={() => { setDevsOpen(!devsOpen); setToolsOpen(false); setMoreOpen(false); }}
                className={`relative flex items-center gap-1 px-3 py-1.5 text-sm rounded-md transition-all duration-200 whitespace-nowrap ${
                  isDevsActive
                    ? scrolled
                      ? "text-ink font-medium bg-slate-100/80"
                      : "text-white font-medium bg-white/10"
                    : scrolled
                    ? "text-slate-500 hover:text-ink hover:bg-slate-50"
                    : "text-white/60 hover:text-white hover:bg-white/5"
                }`}
              >
                Developers
                <svg className={`h-3 w-3 transition-transform duration-200 ${devsOpen ? "rotate-180" : ""}`} fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
                </svg>
                {isDevsActive && (
                  <span className="absolute -bottom-0.5 left-1/2 -translate-x-1/2 w-5 h-[2px] rounded-full bg-[var(--color-gold)]" />
                )}
              </button>

              {devsOpen && (
                <div className="absolute top-full left-1/2 -translate-x-1/2 mt-2 w-[340px] rounded-xl border border-slate-200 bg-white shadow-xl shadow-slate-200/50 p-3 animate-fade-in">
                  <div className="space-y-0.5">
                    {DEVELOPER_LINKS.map((link) => (
                      <Link
                        key={link.href}
                        href={link.href}
                        onClick={() => setDevsOpen(false)}
                        className="flex items-start gap-3 rounded-lg px-3 py-2.5 hover:bg-slate-50 transition-colors group"
                      >
                        <svg
                          className="h-4 w-4 text-slate-400 group-hover:text-brand-500 transition-colors mt-0.5 shrink-0"
                          fill="none"
                          viewBox="0 0 24 24"
                          strokeWidth={1.5}
                          stroke="currentColor"
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" d={link.icon} />
                        </svg>
                        <div>
                          <span className="block text-sm font-medium text-slate-700 group-hover:text-ink transition-colors">
                            {link.label}
                          </span>
                          <span className="block text-xs text-slate-400 mt-0.5">
                            {link.desc}
                          </span>
                        </div>
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* PDF Tools dropdown */}
            <div className="relative" ref={toolsRef}>
              <button
                onClick={() => { setToolsOpen(!toolsOpen); setDevsOpen(false); setMoreOpen(false); }}
                className={`relative flex items-center gap-1 px-3 py-1.5 text-sm rounded-md transition-all duration-200 whitespace-nowrap ${
                  isToolsActive
                    ? scrolled
                      ? "text-ink font-medium bg-slate-100/80"
                      : "text-white font-medium bg-white/10"
                    : scrolled
                    ? "text-slate-500 hover:text-ink hover:bg-slate-50"
                    : "text-white/60 hover:text-white hover:bg-white/5"
                }`}
              >
                PDF Tools
                <svg className={`h-3 w-3 transition-transform duration-200 ${toolsOpen ? "rotate-180" : ""}`} fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
                </svg>
                {isToolsActive && (
                  <span className="absolute -bottom-0.5 left-1/2 -translate-x-1/2 w-5 h-[2px] rounded-full bg-[var(--color-gold)]" />
                )}
              </button>

              {/* Mega-menu dropdown */}
              {toolsOpen && (
                <div className="absolute top-full left-1/2 -translate-x-1/2 mt-2 w-[520px] rounded-xl border border-slate-200 bg-white shadow-xl shadow-slate-200/50 p-5 animate-fade-in">
                  <div className="grid grid-cols-2 gap-x-6 gap-y-4">
                    {TOOL_CATEGORIES.map((cat) => (
                      <div key={cat.label}>
                        <h4 className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider mb-2">
                          {cat.label}
                        </h4>
                        <div className="space-y-0.5">
                          {cat.tools.map((tool) => (
                            <Link
                              key={tool.href}
                              href={tool.href}
                              onClick={() => setToolsOpen(false)}
                              className="block rounded-md px-2.5 py-1.5 text-sm text-slate-600 hover:text-ink hover:bg-slate-50 transition-colors"
                            >
                              {tool.label}
                            </Link>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="mt-4 pt-3 border-t border-slate-100">
                    <Link
                      href="/tools"
                      onClick={() => setToolsOpen(false)}
                      className="flex items-center gap-1.5 text-sm font-medium text-brand-600 hover:text-brand-700 transition-colors"
                    >
                      View all PDF tools
                      <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3" />
                      </svg>
                    </Link>
                  </div>
                </div>
              )}
            </div>

            {/* More dropdown (Compare + Company merged) */}
            <div className="relative" ref={moreRef}>
              <button
                onClick={() => { setMoreOpen(!moreOpen); setToolsOpen(false); setDevsOpen(false); }}
                className={`relative flex items-center gap-1 px-3 py-1.5 text-sm rounded-md transition-all duration-200 whitespace-nowrap ${
                  isMoreActive
                    ? scrolled
                      ? "text-ink font-medium bg-slate-100/80"
                      : "text-white font-medium bg-white/10"
                    : scrolled
                    ? "text-slate-500 hover:text-ink hover:bg-slate-50"
                    : "text-white/60 hover:text-white hover:bg-white/5"
                }`}
              >
                More
                <svg className={`h-3 w-3 transition-transform duration-200 ${moreOpen ? "rotate-180" : ""}`} fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
                </svg>
                {isMoreActive && (
                  <span className="absolute -bottom-0.5 left-1/2 -translate-x-1/2 w-5 h-[2px] rounded-full bg-[var(--color-gold)]" />
                )}
              </button>

              {/* More dropdown panel */}
              {moreOpen && (
                <div className="absolute top-full right-0 mt-2 w-[320px] rounded-xl border border-slate-200 bg-white shadow-xl shadow-slate-200/50 p-3 animate-fade-in">
                  {/* Compare section */}
                  <h4 className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider px-3 mb-1.5">
                    Compare
                  </h4>
                  <div className="space-y-0.5 mb-3">
                    {COMPARE_LINKS.map((link) => (
                      <Link
                        key={link.href}
                        href={link.href}
                        onClick={() => setMoreOpen(false)}
                        className="block rounded-lg px-3 py-2 hover:bg-slate-50 transition-colors group"
                      >
                        <span className="block text-sm font-medium text-slate-700 group-hover:text-ink transition-colors">
                          {link.label}
                        </span>
                        <span className="block text-xs text-slate-400 mt-0.5">
                          {link.desc}
                        </span>
                      </Link>
                    ))}
                  </div>

                  {/* Company section */}
                  <div className="border-t border-slate-100 pt-2.5">
                    <h4 className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider px-3 mb-1.5">
                      Company
                    </h4>
                    <div className="space-y-0.5">
                      {MORE_LINKS.map((link) => (
                        <Link
                          key={link.href}
                          href={link.href}
                          onClick={() => setMoreOpen(false)}
                          className="flex items-start gap-3 rounded-lg px-3 py-2 hover:bg-slate-50 transition-colors group"
                        >
                          <svg
                            className="h-4 w-4 text-slate-400 group-hover:text-brand-500 transition-colors mt-0.5 shrink-0"
                            fill="none"
                            viewBox="0 0 24 24"
                            strokeWidth={1.5}
                            stroke="currentColor"
                          >
                            <path strokeLinecap="round" strokeLinejoin="round" d={link.icon} />
                          </svg>
                          <div>
                            <span className="block text-sm font-medium text-slate-700 group-hover:text-ink transition-colors">
                              {link.label}
                            </span>
                            <span className="block text-xs text-slate-400 mt-0.5">
                              {link.desc}
                            </span>
                          </div>
                        </Link>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Desktop CTA */}
        <div className="hidden lg:flex items-center gap-3">
          <Link
            href="/login"
            className={`px-4 py-2 text-sm font-medium rounded-lg transition-all duration-200 whitespace-nowrap ${
              scrolled
                ? "text-slate-600 hover:text-ink hover:bg-slate-50"
                : "text-white/70 hover:text-white hover:bg-white/5"
            }`}
          >
            Sign in
          </Link>
          <Link href="/register" className="btn-primary text-sm whitespace-nowrap">
            Get started free
          </Link>
        </div>

        {/* Mobile hamburger */}
        <button
          onClick={() => setMobileOpen(!mobileOpen)}
          className={`lg:hidden p-2 rounded-lg transition-colors ${
            scrolled ? "text-slate-600 hover:bg-slate-50" : "text-white/80 hover:bg-white/10"
          }`}
          aria-label="Toggle menu"
        >
          <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
            {mobileOpen ? (
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            ) : (
              <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 9h16.5m-16.5 6.75h16.5" />
            )}
          </svg>
        </button>
      </div>

      {/* Mobile menu */}
      {mobileOpen && (
        <div className={`lg:hidden border-t px-6 pb-4 pt-2 overflow-y-auto max-h-[calc(100dvh-60px)] ${
          scrolled ? "bg-white border-slate-200/60" : "bg-[#060a13]/95 backdrop-blur-xl border-white/10"
        }`}>
          <div className="flex flex-col gap-1">
            {/* Auth buttons at top — always visible without scrolling */}
            <div className="pb-3 mb-1 border-b border-slate-200/20 flex flex-col gap-2">
              <Link href="/login" onClick={() => setMobileOpen(false)} className="btn-ghost text-sm justify-center">
                Sign in
              </Link>
              <Link href="/register" onClick={() => setMobileOpen(false)} className="btn-primary text-sm justify-center">
                Get started free
              </Link>
            </div>

            {NAV_LINKS.map((link) => (
              <Link
                key={link.href}
                href={link.href}
                onClick={() => setMobileOpen(false)}
                className={`px-3 py-2.5 text-sm rounded-lg transition-colors ${
                  pathname === link.href
                    ? scrolled ? "text-ink font-medium bg-slate-50" : "text-white font-medium bg-white/10"
                    : scrolled ? "text-slate-500 hover:text-ink" : "text-white/60 hover:text-white"
                }`}
              >
                {link.label}
              </Link>
            ))}

            {/* Developers in mobile */}
            <div className={`px-3 pt-2 pb-1 text-[10px] font-semibold uppercase tracking-wider ${
              scrolled ? "text-slate-400" : "text-white/30"
            }`}>
              Developers
            </div>
            {DEVELOPER_LINKS.map((link) => (
              <Link
                key={link.href}
                href={link.href}
                onClick={() => setMobileOpen(false)}
                className={`px-3 py-2 text-sm rounded-lg transition-colors ${
                  pathname === link.href
                    ? scrolled ? "text-ink font-medium bg-slate-50" : "text-white font-medium bg-white/10"
                    : scrolled ? "text-slate-500 hover:text-ink" : "text-white/60 hover:text-white"
                }`}
              >
                {link.label}
              </Link>
            ))}

            {/* PDF Tools in mobile */}
            <div className={`px-3 pt-2 pb-1 text-[10px] font-semibold uppercase tracking-wider ${
              scrolled ? "text-slate-400" : "text-white/30"
            }`}>
              PDF Tools
            </div>
            <Link
              href="/sign-pdf-online-free"
              onClick={() => setMobileOpen(false)}
              className={`px-3 py-2 text-sm rounded-lg transition-colors ${
                pathname === "/sign-pdf-online-free"
                  ? scrolled ? "text-ink font-medium bg-slate-50" : "text-white font-medium bg-white/10"
                  : scrolled ? "text-slate-500 hover:text-ink" : "text-white/60 hover:text-white"
              }`}
            >
              Sign PDF Online
            </Link>
            <Link
              href="/tools"
              onClick={() => setMobileOpen(false)}
              className={`px-3 py-2 text-sm rounded-lg transition-colors ${
                pathname === "/tools"
                  ? scrolled ? "text-ink font-medium bg-slate-50" : "text-white font-medium bg-white/10"
                  : scrolled ? "text-slate-500 hover:text-ink" : "text-white/60 hover:text-white"
              }`}
            >
              All PDF Tools
            </Link>

            {/* Compare section in mobile */}
            <div className={`px-3 pt-2 pb-1 text-[10px] font-semibold uppercase tracking-wider ${
              scrolled ? "text-slate-400" : "text-white/30"
            }`}>
              Compare
            </div>
            {COMPARE_LINKS.map((link) => (
              <Link
                key={link.href}
                href={link.href}
                onClick={() => setMobileOpen(false)}
                className={`px-3 py-2 text-sm rounded-lg transition-colors ${
                  pathname === link.href
                    ? scrolled ? "text-ink font-medium bg-slate-50" : "text-white font-medium bg-white/10"
                    : scrolled ? "text-slate-500 hover:text-ink" : "text-white/60 hover:text-white"
                }`}
              >
                {link.label}
              </Link>
            ))}

            {/* Company section in mobile */}
            <div className={`px-3 pt-2 pb-1 text-[10px] font-semibold uppercase tracking-wider ${
              scrolled ? "text-slate-400" : "text-white/30"
            }`}>
              Company
            </div>
            {MORE_LINKS.map((link) => (
              <Link
                key={link.href}
                href={link.href}
                onClick={() => setMobileOpen(false)}
                className={`px-3 py-2 text-sm rounded-lg transition-colors ${
                  pathname === link.href
                    ? scrolled ? "text-ink font-medium bg-slate-50" : "text-white font-medium bg-white/10"
                    : scrolled ? "text-slate-500 hover:text-ink" : "text-white/60 hover:text-white"
                }`}
              >
                {link.label}
              </Link>
            ))}

          </div>
        </div>
      )}
    </nav>
  );
}
